import type { ScoreEntry } from '../services/scoreService';

/**
 * The three leaderboards, and the pure arithmetic behind them.
 *
 * Kept free of Phaser and of Supabase so the scene, the RPC wrapper and the
 * tests all agree on one definition of "a player's score".
 *
 * Spec: docs/superpowers/specs/2026-09-20-leaderboards-design.md
 */

/** The boards, in tab order. Also the `kind` the RPCs validate against. */
export const BOARD_KINDS = ['coins', 'score', 'towers', 'creator', 'rate'] as const;
export type BoardKind = (typeof BOARD_KINDS)[number];

/** Tab labels, and the singular noun used in the empty state. */
export const BOARD_LABELS: Record<BoardKind, string> = {
  coins: 'COINS',
  score: 'SCORE',
  towers: 'TOWERS',
  creator: 'CREATOR',
  rate: 'RATE',
};

/** How many players a board shows. The RPC clamps to this, so it is a real cap. */
export const LEADERBOARD_LIMIT = 50;

/**
 * Highest level id that counts as campaign play.
 *
 * The campaign is ids 1–25, the Hub 101–110, the Origin Event 201–202, the
 * arenas 900/901.
 */
export const CAMPAIGN_ID_MAX = 99;

/**
 * Highest level id whose score counts toward the total: the campaign, the Hub
 * (since 2026-10-04) and the Origin Event (since 2026-10-06). The arenas sit
 * far above it and never count — they are sandboxes. `lb_total_score` sums
 * `where level_id between 1 and 299` because SQL cannot import LEVELS;
 * `tests/unit/leaderboard.test.ts` asserts every campaign, Hub and Origin level
 * is inside and both arenas outside, so a level added at id 350 fails a test
 * instead of quietly leaving the board.
 */
export const SCORED_ID_MAX = 299;

/**
 * The Event Archive's minigame board (spec 2026-10-04). NOT one of
 * BOARD_KINDS: those are the tabs on the LEADERBOARDS screen, and this board is
 * reached only from the end of an archive Capy Run. The RPCs accept it.
 */
export const CAPY_RUN_BOARD = 'capyrun' as const;
/** Every kind the RPCs accept: the three tabs plus the minigame board. */
export type AnyBoardKind = BoardKind | typeof CAPY_RUN_BOARD;

/** One rendered row — exactly the four fields the RPCs return, and nothing else. */
export interface LeaderboardRow {
  rank: number;
  username: string;
  /** Avatar icon key (see config/avatars); '' is the neutral default. */
  avatar: string;
  value: number;
}

export function isBoardKind(k: string): k is BoardKind {
  return (BOARD_KINDS as readonly string[]).includes(k);
}

/**
 * A player's total score: the BEST attempt on each campaign, Hub and Origin level,
 * summed (a Peaceful Hub run is never scored, so it cannot inflate this).
 *
 * `hambi.scores` is an append LOG — `LocalScoreService.submit` pushes every
 * attempt and never de-duplicates — so summing it raw would rank a player who
 * replayed Episode I a hundred times above one who cleared the finales. This
 * reproduces MenuScene's BEST TOTAL exactly, and the SQL mirrors it.
 */
export function totalScore(entries: readonly ScoreEntry[]): number {
  const best = new Map<number, number>();
  for (const e of entries) {
    if (!e || !Number.isInteger(e.levelId)) continue;
    if (e.levelId < 1 || e.levelId > SCORED_ID_MAX) continue;
    if (typeof e.score !== 'number' || !Number.isFinite(e.score) || e.score < 0) continue;
    const score = Math.floor(e.score);
    if (score > (best.get(e.levelId) ?? 0)) best.set(e.levelId, score);
  }
  let total = 0;
  for (const v of best.values()) total += v;
  return total;
}

/** Thousands-grouped, never negative, never fractional. */
export function formatBoardValue(_kind: AnyBoardKind, value: number): string {
  const n = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  return n.toLocaleString('en-US');
}

/** `#1`, `#50`, `#79` — the rank badge at the left of a row. */
export function formatRank(rank: number): string {
  return `#${Math.max(1, Math.floor(rank))}`;
}
